// Add / edit an application: /pam/applications/new, and the Settings tab of
// /pam/applications/:id. Saves through POST /api/v1/idp/apps/save
// (internal/idp/apps.go SaveApp, samlapps.go normaliseSAML).
import React, { useMemo, useRef, useState } from "react";
import { Button, Checkbox, Input, Select, Switch, message } from "antd";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileCode2, FileUp, KeyRound, Server, ShieldCheck, Smartphone, XCircle } from "lucide-react";

import { PageHeader, PageShell } from "../../design-system/tableUi";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../design-system/tokens";
import { saveApp } from "../../service/idpService";
import { FieldLabel, InlineValue, Note, PanelHead, card } from "../enduser/ssh-proxy/sshProxyUi";
import {
  APP_TYPES,
  ErrorText,
  HelpText,
  RotateSecretButton,
  SecretHint,
  SecretModal,
  UriList,
  appTypeKey,
  cleanUris,
  hasRotatableSecret,
  reportIdpError,
  uriListError,
  validLogoUrl,
} from "./appsUi";

const TYPE_ICON = { web: Server, spa: Smartphone, saml: FileCode2, mfa_api: ShieldCheck };
const OPTIONAL_SCOPES = ["profile", "email", "groups"];
const DEFAULT_ATTRS = { email: "email", name: "name", username: "username", groups: "groups" };
const ATTR_FIELDS = [
  ["email", "Email"],
  ["name", "Name"],
  ["username", "Username"],
  ["groups", "Groups"],
];
const NAMEID_FORMATS = [
  { value: "emailAddress", label: "emailAddress" },
  { value: "unspecified", label: "unspecified" },
  { value: "persistent", label: "persistent" },
];
const NAMEID_SOURCES = [
  { value: "email", label: "Email" },
  { value: "upn", label: "UPN" },
  { value: "user_id", label: "AuthNull user ID" },
];

const field = { marginBottom: 16 };
const section = { ...card, padding: 18 };

const draftFrom = (app) => {
  const o = app?.oidc;
  const s = app?.saml;
  return {
    type: appTypeKey(app),
    name: app?.name || "",
    logoUrl: app?.logoUrl || "",
    enabled: app ? !!app.enabled : true,
    redirectUris: o?.redirectUris?.length ? [...o.redirectUris] : [""],
    postLogoutRedirectUris: o?.postLogoutRedirectUris?.length ? [...o.postLogoutRedirectUris] : [""],
    scopes: o?.allowedScopes?.length ? o.allowedScopes.filter((x) => OPTIONAL_SCOPES.includes(x)) : ["profile", "email"],
    requirePkce: o ? !!o.requirePkce : true,
    metadata: "",
    entityId: s?.entityId || "",
    acsUrls: s?.acsUrls?.length ? [...s.acsUrls] : [""],
    nameIdFormat: s?.nameIdFormat || "emailAddress",
    nameIdSource: s?.nameIdSource || "email",
    // An existing app's missing attribute is one it does not send: shown empty.
    attributes: s ? Object.fromEntries(ATTR_FIELDS.map(([k]) => [k, s.attributes?.[k] || ""])) : { ...DEFAULT_ATTRS },
  };
};

// The client-side copy of the API's rules. { field: message }.
const validate = (d) => {
  const e = {};
  const name = d.name.trim();
  if (!name) e.name = "Enter a name.";
  else if ([...name].length > 80) e.name = "At most 80 characters.";
  if (!validLogoUrl(d.logoUrl.trim())) e.logoUrl = "The logo is an https:// URL.";
  if (d.type === "web" || d.type === "spa") {
    const r = uriListError(d.redirectUris, { required: true });
    if (r) e.redirectUris = r;
    const l = uriListError(d.postLogoutRedirectUris, { what: "sign-out redirect URI" });
    if (l) e.postLogoutRedirectUris = l;
  }
  if (d.type === "saml") {
    const hasMeta = !!d.metadata.trim();
    const entity = d.entityId.trim();
    if (!entity && !hasMeta) e.entityId = "Enter the app's entity ID, or import its metadata.";
    else if (entity.length > 1024 || /\s/.test(entity)) e.entityId = "The entity ID has no spaces (at most 1024 characters).";
    const a = uriListError(d.acsUrls, { required: !hasMeta, what: "ACS URL" });
    if (a) e.acsUrls = a;
    if (d.metadata.length > 1024 * 1024) e.metadata = "The metadata is larger than 1 MB.";
    const badAttr = ATTR_FIELDS.find(([k]) => {
      const v = d.attributes[k] || "";
      return v.length > 256 || /[<>"\r\n]/.test(v);
    });
    if (badAttr) e.attributes = `${badAttr[1]}: at most 256 characters, without < > or ".`;
  }
  return e;
};

// The save body: the common fields and only the block for the app's type.
const bodyFrom = (d, app) => {
  const t = APP_TYPES.find((x) => x.key === d.type);
  const body = {
    ...(app?.id ? { id: app.id } : {}),
    name: d.name.trim(),
    protocol: t.protocol,
    logoUrl: d.logoUrl.trim(),
    disabled: !d.enabled,
  };
  if (d.type === "web" || d.type === "spa") {
    // A backend app keeps the method it has (client_secret_post stays post).
    const prev = app?.oidc?.tokenEndpointAuthMethod;
    const method = d.type === "spa" ? "none" : prev && prev !== "none" ? prev : t.method;
    body.oidc = {
      tokenEndpointAuthMethod: method,
      redirectUris: cleanUris(d.redirectUris),
      postLogoutRedirectUris: cleanUris(d.postLogoutRedirectUris),
      allowedScopes: ["openid", ...OPTIONAL_SCOPES.filter((x) => d.scopes.includes(x))],
      requirePkce: d.type === "spa" ? true : d.requirePkce,
    };
  } else if (d.type === "saml") {
    body.saml = {
      entityId: d.entityId.trim(),
      acsUrls: cleanUris(d.acsUrls),
      nameIdFormat: d.nameIdFormat,
      nameIdSource: d.nameIdSource,
      // Empty = not sent: the server drops empty names.
      attributes: Object.fromEntries(ATTR_FIELDS.map(([k]) => [k, (d.attributes[k] || "").trim()])),
      ...(d.metadata.trim() ? { metadata: d.metadata } : {}),
    };
  }
  // mfa_api: the server makes it a confidential client with no redirect URIs
  // and the "mfa" scope, and always issues a secret on create. Only its token
  // endpoint auth method is sent, on edit, so client_secret_post stays post
  // (the server defaults an empty one to client_secret_basic).
  if (d.type === "mfa_api" && app?.oidc?.tokenEndpointAuthMethod && app.oidc.tokenEndpointAuthMethod !== "none") {
    body.oidc = { tokenEndpointAuthMethod: app.oidc.tokenEndpointAuthMethod };
  }
  return body;
};

function TypeCards({ value, onChange, isAllowed }) {
  return (
    // Two by two, so no card sits alone on a row; one column on a phone (index.css).
    <div className="an-app-type-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
      {APP_TYPES.map((t) => {
        const Icon = TYPE_ICON[t.key];
        const active = value === t.key;
        const allowed = isAllowed(t.key);
        return (
          <button
            key={t.key}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={!allowed}
            onClick={() => allowed && onChange(t.key)}
            style={{
              textAlign: "left",
              padding: 14,
              borderRadius: 10,
              border: `1px solid ${active ? AN_COLORS.primary : AN_COLORS.line}`,
              boxShadow: active ? `0 0 0 1px ${AN_COLORS.primary}` : "none",
              background: active ? AN_COLORS.primaryL : AN_COLORS.surface,
              cursor: allowed ? (active ? "default" : "pointer") : "not-allowed",
              opacity: allowed || active ? 1 : 0.5,
              fontFamily: AN_FONT,
              display: "grid",
              gap: 6,
              alignContent: "start",
            }}
          >
            <span
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: active ? AN_COLORS.primary : AN_COLORS.bg,
                color: active ? "white" : AN_COLORS.ink2,
              }}
            >
              <Icon size={16} />
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: AN_COLORS.ink }}>{t.title}</span>
            <span style={{ fontFamily: AN_MONO, fontSize: 10.5, letterSpacing: 0.6, textTransform: "uppercase", color: AN_COLORS.muted }}>
              {t.tech}
            </span>
            <span style={{ fontSize: 12, color: AN_COLORS.muted, lineHeight: 1.45 }}>{t.desc}</span>
          </button>
        );
      })}
    </div>
  );
}

// The rotate-secret card of a confidential client's settings: the secret's
// hint and creation date next to Rotate secret. onRotated reads the app again.
function SecretCard({ app, onRotated }) {
  return (
    <div style={section}>
      <PanelHead
        title="Client secret"
        desc="The app authenticates to AuthNull with its client ID and this secret. It was shown once, when it was made; AuthNull keeps only a hash."
        right={
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
            <SecretHint oidc={app.oidc} />
            <RotateSecretButton app={app} onRotated={onRotated} />
          </div>
        }
      />
    </div>
  );
}

/**
 * The form. `app` is null to create. `onSaved(app)` runs after a save — after
 * the secret dialog is closed, when one was issued. `onSecretRotated()` runs
 * after a rotation, to read the app again.
 */
export function ApplicationSettings({ app, onSaved, onCancel, onNotFound, onSecretRotated }) {
  const editing = !!app;
  const [msg, msgCtx] = message.useMessage();
  const [d, setD] = useState(() => draftFrom(app));
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [saving, setSaving] = useState(false);
  // Only while the dialog is open; cleared when it closes.
  const [secret, setSecret] = useState(null);
  const [savedApp, setSavedApp] = useState(null);
  const fileRef = useRef(null);

  const set = (patch) => {
    setD((prev) => ({ ...prev, ...patch }));
    setServerError("");
  };
  const oidc = d.type === "web" || d.type === "spa";
  const originalType = appTypeKey(app);
  // The protocol cannot change once created. The backend allows switching an
  // OpenID Connect app between backend and single-page (secret made / dropped).
  const isAllowed = (key) =>
    !editing || key === originalType || (["web", "spa"].includes(key) && ["web", "spa"].includes(originalType));

  const save = () => {
    const e = validate(d);
    setErrors(e);
    setServerError("");
    if (Object.keys(e).length) return;
    setSaving(true);
    saveApp(bodyFrom(d, app))
      .then((res) => {
        setSaving(false);
        const saved = res?.app;
        if (res?.clientSecret) {
          setSavedApp(saved);
          setSecret(res.clientSecret);
        } else {
          if (editing) msg.success("Saved.");
          onSaved?.(saved);
        }
      })
      .catch((err) => {
        setSaving(false);
        reportIdpError(err, { msg, fallback: "Could not save the application.", setInline: setServerError, onNotFound });
      });
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set({ metadata: String(reader.result || "") });
    reader.onerror = () => msg.error("Could not read the file.");
    reader.readAsText(file);
  };

  const switchedToSpa = editing && originalType === "web" && d.type === "spa";
  const switchedToWeb = editing && originalType === "spa" && d.type === "web";

  return (
    <div style={{ display: "grid", gap: 16, maxWidth: "56rem" }}>
      {msgCtx}
      <SecretModal
        secret={secret}
        clientId={savedApp?.oidc?.clientId}
        onDone={() => {
          setSecret(null);
          const next = savedApp;
          setSavedApp(null);
          onSaved?.(next);
        }}
      />

      <div style={section}>
        <PanelHead
          title="Application type"
          desc={editing ? "The type can't be changed after the app is created. Create a new app instead." : "Not sure? Look in your app's settings for 'SSO', 'SAML' or 'OpenID Connect'. If it has none and you can change its code, use MFA API."}
        />
        <TypeCards value={d.type} onChange={(type) => set({ type })} isAllowed={isAllowed} />
        {switchedToSpa && (
          <div style={{ marginTop: 12 }}>
            <Note tone="warn">A single-page or mobile app has no secret: saving removes this app's client secret.</Note>
          </div>
        )}
        {switchedToWeb && (
          <div style={{ marginTop: 12 }}>
            <Note tone="info">Saving issues a client secret for this app. It is shown once.</Note>
          </div>
        )}
      </div>

      <div style={section}>
        <PanelHead title="Details" />
        <div style={field}>
          <FieldLabel required>Name</FieldLabel>
          <Input
            value={d.name}
            maxLength={80}
            placeholder="e.g. ERP"
            status={errors.name ? "error" : undefined}
            onChange={(e) => set({ name: e.target.value })}
          />
          <ErrorText>{errors.name}</ErrorText>
        </div>
        <div style={field}>
          <FieldLabel hint="optional, https://">Logo URL</FieldLabel>
          <Input
            value={d.logoUrl}
            placeholder="https://app.example.com/logo.png"
            style={{ fontFamily: AN_MONO }}
            status={errors.logoUrl ? "error" : undefined}
            onChange={(e) => set({ logoUrl: e.target.value })}
          />
          <ErrorText>{errors.logoUrl}</ErrorText>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Switch checked={d.enabled} onChange={(enabled) => set({ enabled })} />
          <span style={{ fontSize: 13, color: AN_COLORS.ink2 }}>
            {d.enabled ? "Enabled: assigned users can sign in" : "Disabled: nobody can sign in to this app"}
          </span>
        </div>
      </div>

      {oidc && (
        <div style={section}>
          <PanelHead title="OpenID Connect" />
          <div style={field}>
            <FieldLabel required>Redirect URIs</FieldLabel>
            <UriList
              value={d.redirectUris}
              onChange={(redirectUris) => set({ redirectUris })}
              placeholder="https://app.example.com/callback"
              addLabel="Add redirect URI"
              status={errors.redirectUris ? "error" : undefined}
            />
            <HelpText>
              Where AuthNull sends the user back after sign-in. Matched exactly, character for character: no
              wildcards (<InlineValue>*</InlineValue>) and no fragments (<InlineValue>#</InlineValue>).{" "}
              <InlineValue>https://</InlineValue> only; <InlineValue>http://localhost</InlineValue> is allowed for development.
            </HelpText>
            <ErrorText>{errors.redirectUris}</ErrorText>
          </div>
          <div style={field}>
            <FieldLabel hint="optional">Sign-out redirect URIs</FieldLabel>
            <UriList
              value={d.postLogoutRedirectUris}
              onChange={(postLogoutRedirectUris) => set({ postLogoutRedirectUris })}
              placeholder="https://app.example.com/signed-out"
              addLabel="Add sign-out URI"
              status={errors.postLogoutRedirectUris ? "error" : undefined}
            />
            <HelpText>Where the user may land after signing out. Same rules, matched exactly.</HelpText>
            <ErrorText>{errors.postLogoutRedirectUris}</ErrorText>
          </div>
          <div style={field}>
            <FieldLabel>Scopes the app may request</FieldLabel>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <Checkbox checked disabled>
                <span style={{ fontFamily: AN_MONO, fontSize: 12.5 }}>openid</span>
              </Checkbox>
              {OPTIONAL_SCOPES.map((sc) => (
                <Checkbox
                  key={sc}
                  checked={d.scopes.includes(sc)}
                  onChange={(e) => set({ scopes: e.target.checked ? [...d.scopes, sc] : d.scopes.filter((x) => x !== sc) })}
                >
                  <span style={{ fontFamily: AN_MONO, fontSize: 12.5 }}>{sc}</span>
                </Checkbox>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Switch
              checked={d.type === "spa" ? true : d.requirePkce}
              disabled={d.type === "spa"}
              onChange={(requirePkce) => set({ requirePkce })}
            />
            <span style={{ fontSize: 13, color: AN_COLORS.ink2 }}>
              Require PKCE
              {d.type === "spa" && <span style={{ color: AN_COLORS.muted }}> (always on for single-page and mobile apps)</span>}
            </span>
          </div>
        </div>
      )}

      {d.type === "saml" && (
        <div style={section}>
          <PanelHead title="SAML" desc="SP-initiated sign-in, HTTP-POST binding. AuthNull signs both the response and the assertion." />

          <div style={field}>
            <FieldLabel hint="optional">Import from metadata</FieldLabel>
            <Input.TextArea
              value={d.metadata}
              onChange={(e) => set({ metadata: e.target.value })}
              placeholder='<md:EntityDescriptor entityID="https://app.example.com/saml" …>'
              autoSize={{ minRows: 3, maxRows: 10 }}
              spellCheck={false}
              status={errors.metadata ? "error" : undefined}
              style={{ fontFamily: AN_MONO, fontSize: 12 }}
            />
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6, flexWrap: "wrap" }}>
              <input
                ref={fileRef}
                type="file"
                accept=".xml,text/xml,application/xml,application/samlmetadata+xml"
                onChange={onFile}
                style={{ display: "none" }}
              />
              <Button size="small" icon={<FileUp size={13} style={{ verticalAlign: -2 }} />} onClick={() => fileRef.current?.click()}>
                Upload .xml
              </Button>
              {d.metadata && (
                <Button size="small" type="link" onClick={() => set({ metadata: "" })} style={{ padding: 0 }}>
                  Clear
                </Button>
              )}
            </div>
            <HelpText>
              Paste the app's SP metadata or upload its .xml file. When saved, AuthNull fills the Entity ID and the
              HTTP-POST ACS URLs from it if those fields are empty.
            </HelpText>
            <ErrorText>{errors.metadata}</ErrorText>
          </div>

          <div style={field}>
            <FieldLabel required={!d.metadata.trim()}>Entity ID</FieldLabel>
            <Input
              value={d.entityId}
              placeholder="https://app.example.com/saml"
              style={{ fontFamily: AN_MONO }}
              status={errors.entityId ? "error" : undefined}
              onChange={(e) => set({ entityId: e.target.value })}
            />
            <HelpText>The app's Issuer (its service provider entity ID). No spaces.</HelpText>
            <ErrorText>{errors.entityId}</ErrorText>
          </div>

          <div style={field}>
            <FieldLabel required={!d.metadata.trim()}>ACS URLs</FieldLabel>
            <UriList
              value={d.acsUrls}
              onChange={(acsUrls) => set({ acsUrls })}
              placeholder="https://app.example.com/saml/acs"
              addLabel="Add ACS URL"
              status={errors.acsUrls ? "error" : undefined}
            />
            <HelpText>
              Where the app receives the assertion (HTTP-POST binding only). Matched exactly; <InlineValue>https://</InlineValue>{" "}
              only, <InlineValue>http://localhost</InlineValue> for development; no <InlineValue>*</InlineValue> or{" "}
              <InlineValue>#</InlineValue>.
            </HelpText>
            <ErrorText>{errors.acsUrls}</ErrorText>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(14rem, 1fr))", gap: 12 }}>
            <div style={field}>
              <FieldLabel>NameID format</FieldLabel>
              <Select value={d.nameIdFormat} options={NAMEID_FORMATS} onChange={(nameIdFormat) => set({ nameIdFormat })} style={{ width: "100%" }} />
            </div>
            <div style={field}>
              <FieldLabel>NameID value</FieldLabel>
              <Select value={d.nameIdSource} options={NAMEID_SOURCES} onChange={(nameIdSource) => set({ nameIdSource })} style={{ width: "100%" }} />
              <HelpText>
                <InlineValue>user_id</InlineValue> stays the same even if the email changes.
              </HelpText>
            </div>
          </div>

          <div>
            <FieldLabel hint="empty = not sent">Attribute names</FieldLabel>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(11rem, 1fr))", gap: 10 }}>
              {ATTR_FIELDS.map(([k, label]) => (
                <div key={k}>
                  <div style={{ fontSize: 11.5, color: AN_COLORS.muted, marginBottom: 4 }}>{label}</div>
                  <Input
                    value={d.attributes[k]}
                    maxLength={256}
                    placeholder="not sent"
                    style={{ fontFamily: AN_MONO }}
                    status={errors.attributes ? "error" : undefined}
                    onChange={(e) => set({ attributes: { ...d.attributes, [k]: e.target.value } })}
                  />
                </div>
              ))}
            </div>
            <HelpText>The names of the attributes AuthNull puts in the assertion for the user's email, name, username and groups.</HelpText>
            <ErrorText>{errors.attributes}</ErrorText>
          </div>
        </div>
      )}

      {d.type === "mfa_api" && (
        <Note tone="info" icon={<KeyRound size={15} />}>
          An MFA API app has no redirect URIs or scopes: it keeps its own login and calls AuthNull from its server for the
          second factor, with its client ID and secret. {editing ? "" : "A client secret is issued when you create it, and shown once."}
        </Note>
      )}

      {editing && hasRotatableSecret(app) && originalType === d.type && <SecretCard app={app} onRotated={onSecretRotated} />}

      {serverError && (
        <Note tone="danger" icon={<XCircle size={15} />}>
          {serverError}
        </Note>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        )}
        <Button type="primary" loading={saving} onClick={save}>
          {editing ? "Save changes" : "Add application"}
        </Button>
      </div>
    </div>
  );
}

// /pam/applications/new
export default function ApplicationForm() {
  const navigate = useNavigate();
  const back = useMemo(() => () => navigate("/pam/applications"), [navigate]);
  return (
    <PageShell>
      <PageHeader
        eyebrow="Applications"
        title="Add application"
        subtitle="Register an app your users sign in to through AuthNull, or that asks AuthNull for the second factor."
        actions={
          <Button onClick={back} icon={<ArrowLeft size={14} style={{ verticalAlign: -2 }} />}>
            Applications
          </Button>
        }
      />
      <ApplicationSettings
        app={null}
        onCancel={back}
        onSaved={(saved) =>
          saved?.id ? navigate(`/pam/applications/${saved.id}?tab=connect`, { replace: true }) : back()
        }
      />
    </PageShell>
  );
}
